import React, { useState, useEffect } from 'react';
import { FaBullhorn, FaFilter, FaUserFriends, FaDatabase, FaUsers, FaHandshake, FaLightbulb, FaClipboardCheck, FaFileAlt, FaCode } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const SEASProcessFlow = ({ type }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [hoveredStep, setHoveredStep] = useState(null);

  let steps = [
    { icon: <FaBullhorn />, label: "Advertising" },
    { icon: <FaFilter />, label: "Candidates are Screened" },
    { icon: <FaUserFriends />, label: "Interview" },
    { icon: <FaDatabase />, label: "Reference Data" },
    { icon: <FaUsers />, label: "Talent Pool" },
  ];

  // Replace with Managed Services steps if applicable
  if (type === "managed-services") {
    steps = [
      { icon: <FaHandshake />, label: "Meeting" },
      { icon: <FaLightbulb />, label: "Suggestions" },
      { icon: <FaClipboardCheck />, label: "Decisions" },
      { icon: <FaFileAlt />, label: "Requirement Sheet" },
      { icon: <FaCode />, label: "Development Starts" },
    ];
  }

  // Web Development Services - With Links
  const webDevelopmentProjects = [
    {
      title: "Web Development",
      url: "#",
      isHeader: true,
    },
    // {
    //   title: "wardlewis.co.uk",
    //   url: "https://wardlewis.co.uk/",
    // },
    {
      title: "drinkshanti.com",
      url: "https://drinkshanti.com/",
    },
    // {
    //   title: "solutioneu.com",
    //   url: "https://solutioneu.com/",
    // },
    {
      title: "brackleyselfstorage.co.uk",
      url: "https://brackleyselfstorage.co.uk/",
    },
    {
      title: "oxfordcityflooring.co.uk",
      url: "https://oxfordcityflooring.co.uk/",
    },
    {
      title: "cybersoftvantage.com",
      url: "https://www.cybersoftvantage.com/",
    },
    {
      title: "ctekinc.com",
      url: "https://ctekinc.com/",
    },
  ];

  useEffect(() => {
    // Only auto-advance if not hovering on any step
    if (!isHovering) {
      const timer = setInterval(() => {
        setActiveStep((prev) => (prev + 1) % steps.length);
      }, 2500);
      return () => clearInterval(timer);
    }
  }, [isHovering, steps.length]);

  const handleStepHover = (index) => {
    setIsHovering(true);
    setHoveredStep(index);
    setActiveStep(index); // Set the active step to the hovered one
  };

  const handleStepLeave = () => {
    setIsHovering(false);
    setHoveredStep(null);
  };

  return (
    <div className="seas-flow-container">
      <div className="flow-header">
        {type === "managed-services" ? (
          <>
            <h2>Our Development Process</h2>
            <p>
              From idea to execution, every project follows these clear steps.
            </p>
          </>
        ) : (
          <>
            <h2>We Hire Professionals</h2>
            <p>With Minimum a Bachelor's Degree & 1-2 Years of Experience</p>
          </>
        )}
      </div>

      <div className="process-track">
        {steps.map((step, index) => (
          <React.Fragment key={index}>
            <div className="step-container">
              <motion.div
                className="step-icon"
                animate={{
                  scale: activeStep === index ? 1.1 : 1,
                  backgroundColor: activeStep === index ? "#06a3c2" : "#e6f7fa",
                }}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 15,
                }}
                whileHover={{ scale: 1.05 }}
                onMouseEnter={() => handleStepHover(index)}
                onMouseLeave={handleStepLeave}
              >
                <motion.div
                  className="icon-wrapper"
                  animate={{
                    color: activeStep === index ? "white" : "#06a3c2",
                  }}
                  transition={{ duration: 0.2 }}
                >
                  {React.cloneElement(step.icon, { size: 28 })}
                </motion.div>
              </motion.div>

              <motion.div
                className="step-label"
                animate={{
                  color: activeStep === index ? "#06a3c2" : "#666",
                  fontWeight: activeStep === index ? "600" : "400",
                }}
                onMouseEnter={() => handleStepHover(index)}
                onMouseLeave={handleStepLeave}
              >
                {step.label}
              </motion.div>
            </div>

            {index < steps.length - 1 && (
              <motion.div
                className="connector"
                animate={{
                  opacity: activeStep > index ? 1 : 0.3,
                  backgroundColor: activeStep > index ? "#06a3c2" : "#e6f7fa",
                }}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={isHovering ? `hover-${hoveredStep}` : `auto-${activeStep}`}
          className="step-description"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
        >
          {getStepDescription(type, isHovering ? hoveredStep : activeStep)}
        </motion.div>
      </AnimatePresence>

      {/* Web Development Titles Section - With Links for only managed services */}
      {type === "managed-services"&& (
        <>
      <h2>Websites We Developed</h2>
      <div className="web-development-titles-section">
        {/* <div className="web-dev-header">
          <h2>Web Development</h2>
        </div> */}

        <div className="web-dev-pills">
          {webDevelopmentProjects.slice(1).map((project, index) => (
            <motion.a
              key={index}
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
              className="pill-item"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.05 }}
              onClick={(e) => {
                if (project.isHeader) {
                  e.preventDefault(); // Prevent navigation for header items
                }
              }}
            >
              <span className="project-number">{index + 1})</span>{" "}
              {project.title}
            </motion.a>
          ))}
        </div>
      </div>
      </>
      )}
    </div>
  );
};

// function getStepDescription(step) {
//     const descriptions = [
//         "Advertising of positions is done on multiple platforms.",
//         "Each candidate undergoes rigorous screening for qualifications.",
//         "Shortlisted candidates complete an interview round.",
//         "We verify background information and references.",
//         "Successful candidates join our exclusive talent pool."
//     ];
//     return descriptions[step];
// }
function getStepDescription(type, step) {
    if (type === "managed-services") {
        const descriptions = [
            "We meet with our clients to identify their requirements.",
            "Our technical team suggests improvements and walks clients through different language structures.",
            "Language, technicalities, and visuals are mutually decided.",
            "A requirement sheet is sought and timeline is decided."
        ];
        return descriptions[step];
    }

    const defaultDescriptions = [
      "Advertising of positions is done on multiple platforms.",
      "Each candidate undergoes rigorous screening for qualifications.",
      "Shortlisted candidates complete an interview round.",
      "We verify background information/references and an assessment report is made.",
      "Successful candidates join our exclusive talent pool.",
    ];
    return defaultDescriptions[step];
}

export default SEASProcessFlow;